import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime, PROFILE_LABELS } from '../lib/utils';
import { apiErrorMessage } from '../lib/apiErrors';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Shield, 
  Key, 
  User, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Copy,
  RefreshCw,
  Search,
  FileText,
  Loader2,
  UserPlus,
  Trash2,
  Link as LinkIcon,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const AdminSeguranca = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pendingCodes, setPendingCodes] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  
  // Authorization codes state
  const [authCodes, setAuthCodes] = useState([]);
  const [authCodesTotal, setAuthCodesTotal] = useState(0);
  const [authCodesPage, setAuthCodesPage] = useState(1);
  const [showUsedAuthCodes, setShowUsedAuthCodes] = useState(false);
  const [generatingAuthCode, setGeneratingAuthCode] = useState(false);
  const [authCodeDialogOpen, setAuthCodeDialogOpen] = useState(false);
  const [generatedAuthCode, setGeneratedAuthCode] = useState(null);
  const [authCodeEmailRestriction, setAuthCodeEmailRestriction] = useState('');
  const [authCodeValidityDays, setAuthCodeValidityDays] = useState(7);
  const [authCodeProfile, setAuthCodeProfile] = useState('profissional');
  
  // Audit log filters
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  useEffect(() => {
    // Check if user is master_admin
    if (user && user.perfil !== 'master_admin') {
      toast.error('Acesso restrito ao Master Admin');
      navigate('/dashboard');
      return;
    }
    
    fetchData();
  }, [user, navigate]);

  useEffect(() => {
    if (user?.perfil === 'master_admin') {
      fetchAuditLogs();
    }
  }, [auditPage, actionFilter, userFilter]);

  useEffect(() => {
    if (user?.perfil === 'master_admin') {
      fetchAuthorizationCodes();
    }
  }, [authCodesPage, showUsedAuthCodes]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, codesRes] = await Promise.all([
        api.get('/security/users-list'),
        api.get('/security/pending-codes')
      ]);
      setUsers(usersRes.data);
      setPendingCodes(codesRes.data);
      await Promise.all([fetchAuditLogs(), fetchAuthorizationCodes()]);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso restrito ao Master Admin');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: auditPage.toString(),
        limit: '20'
      });
      if (actionFilter) params.append('action_filter', actionFilter);
      if (userFilter) params.append('user_filter', userFilter);
      
      const response = await api.get(`/security/audit-logs?${params}`);
      setAuditLogs(response.data.logs);
      setAuditTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const fetchAuthorizationCodes = async () => {
    try {
      const params = new URLSearchParams({
        page: authCodesPage.toString(),
        limit: '20',
        show_used: showUsedAuthCodes.toString()
      });
      
      const response = await api.get(`/security/authorization-codes?${params}`);
      setAuthCodes(response.data.codes);
      setAuthCodesTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching authorization codes:', error);
    }
  };

  const handleGenerateCode = async () => {
    if (!selectedUser) {
      toast.error('Selecione um usuário');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/security/generate-code', {
        user_email: selectedUser
      });
      setGeneratedCode(response.data);
      setCodeDialogOpen(true);
      toast.success('Código gerado com sucesso!');
      
      // Refresh pending codes
      const codesRes = await api.get('/security/pending-codes');
      setPendingCodes(codesRes.data);
      
      // Refresh audit logs
      await fetchAuditLogs();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao gerar código';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAuthorizationCode = async () => {
    setGeneratingAuthCode(true);
    try {
      const response = await api.post('/security/generate-authorization-code', {
        email_restrito: authCodeEmailRestriction.trim() || null,
        dias_validade: authCodeValidityDays,
        perfil: authCodeProfile
      });
      setGeneratedAuthCode(response.data);
      setAuthCodeDialogOpen(true);
      toast.success('Código de autorização gerado com sucesso!');
      
      // Clear form
      setAuthCodeEmailRestriction('');
      setAuthCodeValidityDays(7);
      setAuthCodeProfile('profissional');
      
      // Refresh codes list
      await fetchAuthorizationCodes();
      await fetchAuditLogs();
    } catch (error) {
      const message = apiErrorMessage(error, 'Erro ao gerar código');
      toast.error(message);
    } finally {
      setGeneratingAuthCode(false);
    }
  };

  const handleRevokeAuthCode = async (codeId) => {
    if (!confirm('Tem certeza que deseja revogar este código?')) return;
    
    try {
      await api.delete(`/security/authorization-codes/${codeId}`);
      toast.success('Código revogado com sucesso');
      await fetchAuthorizationCodes();
      await fetchAuditLogs();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao revogar código';
      toast.error(message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência');
  };

  const copyInviteLink = (code) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/register#code=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(link);
    toast.success('Link de convite copiado!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  const getActionBadge = (action) => {
    const colors = {
      'SECURITY_CODE_GENERATED': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'PASSWORD_RESET_SUCCESS': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'PASSWORD_RESET_FAILED': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'PASSWORD_RESET_RATE_LIMITED': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'MASTER_ADMIN_CREATED': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'AUTHORIZATION_CODE_GENERATED': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      'AUTHORIZATION_CODE_REVOKED': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      'USER_REGISTERED': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'REGISTRATION_INVALID_CODE': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'REGISTRATION_CODE_EXPIRED': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'REGISTRATION_RATE_LIMITED': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    };
    return colors[action] || 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
  };

  const getActionLabel = (action) => {
    const labels = {
      'SECURITY_CODE_GENERATED': 'Código Senha Gerado',
      'PASSWORD_RESET_SUCCESS': 'Senha Alterada',
      'PASSWORD_RESET_FAILED': 'Falha Alteração Senha',
      'PASSWORD_RESET_RATE_LIMITED': 'Rate Limit Senha',
      'MASTER_ADMIN_CREATED': 'Master Admin Criado',
      'SECURITY_CODE_GENERATION_FAILED': 'Falha Gerar Código',
      'AUTHORIZATION_CODE_GENERATED': 'Código Convite Gerado',
      'AUTHORIZATION_CODE_REVOKED': 'Código Convite Revogado',
      'USER_REGISTERED': 'Usuário Cadastrado',
      'REGISTRATION_INVALID_CODE': 'Código Cadastro Inválido',
      'REGISTRATION_CODE_EXPIRED': 'Código Cadastro Expirado',
      'REGISTRATION_RATE_LIMITED': 'Rate Limit Cadastro',
      'REGISTRATION_EMAIL_MISMATCH': 'E-mail Não Corresponde',
    };
    return labels[action] || action;
  };

  const getAuthCodeStatusBadge = (status) => {
    const styles = {
      'ativo': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'usado': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'expirado': 'bg-gray-100 text-gray-500 dark:bg-gray-900/30 dark:text-gray-400',
    };
    const labels = {
      'ativo': 'Ativo',
      'usado': 'Usado',
      'expirado': 'Expirado',
    };
    return (
      <Badge className={styles[status] || styles.expirado}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-seguranca-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-red-600" />
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
                Admin de Segurança
              </h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Gerenciamento de códigos de segurança, convites e auditoria
            </p>
          </div>
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
            <Shield className="h-3 w-3 mr-1" />
            Acesso Master Admin
          </Badge>
        </div>

        <Tabs defaultValue="authorization" className="space-y-4">
          <TabsList className="flex-wrap">
            <TabsTrigger value="authorization">
              <UserPlus className="h-4 w-4 mr-2" />
              Códigos de Cadastro
            </TabsTrigger>
            <TabsTrigger value="generate">
              <Key className="h-4 w-4 mr-2" />
              Códigos de Senha
            </TabsTrigger>
            <TabsTrigger value="pending">
              <Clock className="h-4 w-4 mr-2" />
              Pendentes ({pendingCodes.length})
            </TabsTrigger>
            <TabsTrigger value="audit">
              <FileText className="h-4 w-4 mr-2" />
              Auditoria
            </TabsTrigger>
          </TabsList>

          {/* Authorization Codes Tab (NEW) */}
          <TabsContent value="authorization" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Gerar Código de Autorização de Cadastro
                </CardTitle>
                <CardDescription>
                  Gere códigos para permitir que novos usuários se cadastrem no sistema. Cada código é de uso único.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-cyan-50 border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800">
                  <UserPlus className="h-4 w-4 text-cyan-600" />
                  <AlertDescription className="text-cyan-700 dark:text-cyan-400 text-sm">
                    <strong>Como funciona:</strong> Sem um código de autorização válido, nenhum novo usuário poderá se cadastrar no sistema. Códigos são de uso único e expiram automaticamente.
                  </AlertDescription>
                </Alert>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="invite-profile">Perfil autorizado</Label>
                    <Select value={authCodeProfile} onValueChange={setAuthCodeProfile}>
                      <SelectTrigger id="invite-profile" data-testid="invite-profile-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent data-testid="invite-profile-options">
                        {Object.entries(PROFILE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value} data-testid={`invite-profile-${value.replaceAll('_', '-')}`}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-restriction">E-mail restrito (opcional)</Label>
                    <Input
                      id="email-restriction"
                      data-testid="invite-email-restriction"
                      type="email"
                      placeholder="usuario@empresa.com"
                      value={authCodeEmailRestriction}
                      onChange={(e) => setAuthCodeEmailRestriction(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Se preenchido, o código só funcionará para este e-mail
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="validity-days">Validade (dias)</Label>
                    <Select 
                      value={authCodeValidityDays.toString()} 
                      onValueChange={(v) => setAuthCodeValidityDays(parseInt(v))}
                    >
                      <SelectTrigger id="validity-days" data-testid="invite-validity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 dia</SelectItem>
                        <SelectItem value="3">3 dias</SelectItem>
                        <SelectItem value="7">7 dias</SelectItem>
                        <SelectItem value="14">14 dias</SelectItem>
                        <SelectItem value="30">30 dias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <Button 
                      onClick={handleGenerateAuthorizationCode} 
                      data-testid="generate-invite-button"
                      disabled={generatingAuthCode}
                      className="w-full"
                    >
                      {generatingAuthCode ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Gerar Código de Cadastro
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Authorization Codes List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="h-5 w-5" />
                      Códigos de Autorização
                    </CardTitle>
                    <CardDescription>
                      {authCodesTotal} código(s) encontrado(s)
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="show-used" 
                        checked={showUsedAuthCodes}
                        onCheckedChange={setShowUsedAuthCodes}
                      />
                      <Label htmlFor="show-used" className="text-sm">Mostrar usados</Label>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchAuthorizationCodes}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {authCodes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum código de autorização encontrado
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>E-mail Restrito</TableHead>
                          <TableHead>Perfil autorizado</TableHead>
                          <TableHead>Validade</TableHead>
                          <TableHead>Criado em</TableHead>
                          <TableHead>Expira em</TableHead>
                          <TableHead>Usado por</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {authCodes.map((code) => (
                          <TableRow key={code.id}>
                            <TableCell>{getAuthCodeStatusBadge(code.status)}</TableCell>
                            <TableCell>
                              {code.email_restrito ? (
                                <div className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {code.email_restrito}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Qualquer e-mail</span>
                              )}
                            </TableCell>
                            <TableCell data-testid={`invite-role-${code.id}`}>{PROFILE_LABELS[code.perfil || 'profissional']}</TableCell>
                            <TableCell>{code.dias_validade} dias</TableCell>
                            <TableCell className="text-sm">{formatDate(code.created_at)}</TableCell>
                            <TableCell className="text-sm">{formatDate(code.expires_at)}</TableCell>
                            <TableCell>
                              {code.used_by_email ? (
                                <div className="flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                                  {code.used_by_email}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {code.status === 'ativo' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevokeAuthCode(code.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Pagination */}
                {authCodesTotal > 20 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAuthCodesPage(p => Math.max(1, p - 1))}
                      disabled={authCodesPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center px-4 text-sm">
                      Página {authCodesPage} de {Math.ceil(authCodesTotal / 20)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAuthCodesPage(p => p + 1)}
                      disabled={authCodesPage >= Math.ceil(authCodesTotal / 20)}
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Generate Password Reset Code Tab */}
          <TabsContent value="generate" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Gerar Código de Recuperação de Senha
                </CardTitle>
                <CardDescription>
                  Gere um código único para o usuário redefinir sua senha. O código expira em 15 minutos.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-400 text-sm">
                    <strong>Atenção:</strong> O código será exibido apenas uma vez. Anote-o e forneça ao usuário de forma segura.
                  </AlertDescription>
                </Alert>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="user-select">Selecionar Usuário</Label>
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                      <SelectTrigger id="user-select">
                        <SelectValue placeholder="Escolha um usuário..." />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.email}>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              <span>{u.nome}</span>
                              <span className="text-muted-foreground">({u.email})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <Button 
                      onClick={handleGenerateCode} 
                      disabled={!selectedUser || generating}
                      className="w-full md:w-auto"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <Key className="mr-2 h-4 w-4" />
                          Gerar Código de Senha
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Users List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Usuários do Sistema
                </CardTitle>
                <CardDescription>
                  {users.length} usuário(s) disponível(is)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>E-mail</TableHead>
                        <TableHead>Perfil</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.nome}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{u.perfil}</Badge>
                          </TableCell>
                          <TableCell>
                            {u.ativo ? (
                              <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                            ) : (
                              <Badge variant="destructive">Inativo</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(u.email);
                                handleGenerateCode();
                              }}
                              disabled={generating}
                            >
                              <Key className="h-4 w-4 mr-1" />
                              Gerar Código
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Codes Tab */}
          <TabsContent value="pending" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Códigos de Senha Pendentes
                </CardTitle>
                <CardDescription>
                  Códigos de recuperação de senha que ainda não foram utilizados
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingCodes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum código pendente
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Usuário</TableHead>
                          <TableHead>Gerado por</TableHead>
                          <TableHead>Criado em</TableHead>
                          <TableHead>Expira em</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingCodes.map((code) => (
                          <TableRow key={code.id}>
                            <TableCell className="font-medium">{code.user_email}</TableCell>
                            <TableCell>{code.generated_by_email}</TableCell>
                            <TableCell>{formatDate(code.created_at)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-amber-50 text-amber-700">
                                <Clock className="h-3 w-3 mr-1" />
                                {formatDate(code.expires_at)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Log de Auditoria
                </CardTitle>
                <CardDescription>
                  Histórico de eventos de segurança ({auditTotal} registro(s))
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="action-filter">Filtrar por ação</Label>
                    <Select value={actionFilter} onValueChange={setActionFilter}>
                      <SelectTrigger id="action-filter">
                        <SelectValue placeholder="Todas as ações" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Todas as ações</SelectItem>
                        <SelectItem value="AUTHORIZATION_CODE">Códigos de Cadastro</SelectItem>
                        <SelectItem value="USER_REGISTERED">Usuários Cadastrados</SelectItem>
                        <SelectItem value="SECURITY_CODE">Códigos de Senha</SelectItem>
                        <SelectItem value="PASSWORD_RESET">Reset de Senha</SelectItem>
                        <SelectItem value="RATE_LIMITED">Rate Limited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="user-filter">Filtrar por usuário</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="user-filter"
                        placeholder="E-mail do usuário..."
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex items-end">
                    <Button variant="outline" onClick={fetchAuditLogs}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Atualizar
                    </Button>
                  </div>
                </div>

                {/* Logs Table */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Ação</TableHead>
                        <TableHead>Executado por</TableHead>
                        <TableHead>Usuário Alvo</TableHead>
                        <TableHead>Detalhes</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">
                            {formatDate(log.timestamp)}
                          </TableCell>
                          <TableCell>
                            <Badge className={getActionBadge(log.action)}>
                              {getActionLabel(log.action)}
                            </Badge>
                          </TableCell>
                          <TableCell>{log.actor_email}</TableCell>
                          <TableCell>{log.target_user_email || '-'}</TableCell>
                          <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                            {typeof log.details === 'object' && log.details ? JSON.stringify(log.details) : (log.details || '-')}
                          </TableCell>
                          <TableCell>
                            {log.success ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {auditTotal > 20 && (
                  <div className="flex justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                      disabled={auditPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center px-4 text-sm">
                      Página {auditPage} de {Math.ceil(auditTotal / 20)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAuditPage(p => p + 1)}
                      disabled={auditPage >= Math.ceil(auditTotal / 20)}
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Generated Password Reset Code Dialog */}
        <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Código de Senha Gerado
              </DialogTitle>
              <DialogDescription>
                Anote este código e forneça ao usuário de forma segura. Ele não será exibido novamente.
              </DialogDescription>
            </DialogHeader>
            
            {generatedCode && (
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                  <AlertDescription className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Usuário:</p>
                      <p className="font-medium">{generatedCode.user_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Código de Segurança:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-2xl font-bold tracking-widest bg-white dark:bg-gray-900 px-4 py-2 rounded border">
                          {generatedCode.code}
                        </code>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => copyToClipboard(generatedCode.code)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Expira em:</p>
                      <p className="font-medium">{formatDate(generatedCode.expires_at)}</p>
                    </div>
                  </AlertDescription>
                </Alert>

                <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-400 text-sm">
                    Este código é de uso único e expira em 15 minutos.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => setCodeDialogOpen(false)}>
                Entendido
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generated Authorization Code Dialog */}
        <Dialog open={authCodeDialogOpen} onOpenChange={setAuthCodeDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="generated-invite-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Código de Autorização Gerado
              </DialogTitle>
              <DialogDescription>
                Este código permite que um novo usuário se cadastre no sistema. Compartilhe de forma segura.
              </DialogDescription>
            </DialogHeader>
            
            {generatedAuthCode && (
              <div className="space-y-4">
                <p className="font-medium" data-testid="generated-invite-profile">
                  Perfil autorizado: {PROFILE_LABELS[generatedAuthCode.perfil] || generatedAuthCode.perfil}
                </p>
                <Alert className="bg-cyan-50 border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800">
                  <AlertDescription className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Código de Autorização:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xl font-bold tracking-widest bg-white dark:bg-gray-900 px-4 py-2 rounded border">
                          {generatedAuthCode.code}
                        </code>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => copyToClipboard(generatedAuthCode.code)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {generatedAuthCode.email_restrito && (
                      <div>
                        <p className="text-sm text-muted-foreground">E-mail restrito:</p>
                        <p className="font-medium">{generatedAuthCode.email_restrito}</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-sm text-muted-foreground">Expira em:</p>
                      <p className="font-medium">{formatDate(generatedAuthCode.expires_at)}</p>
                    </div>
                  </AlertDescription>
                </Alert>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => copyToClipboard(generatedAuthCode.code)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar Código
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => copyInviteLink(generatedAuthCode.code)}
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Copiar Link de Convite
                  </Button>
                </div>

                <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-400 text-sm">
                    Este código é de uso único e será invalidado após o cadastro do usuário.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => setAuthCodeDialogOpen(false)}>
                Entendido
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminSeguranca;
